import styled, { css } from 'styled-components/macro';
import { ScAnimatedPlaceholder } from '../primitives/styled';
import type { InputStructureProps } from './InputStructure';

type ScInputStructureProps = Omit<InputStructureProps, 'error'> & {
  error?: boolean;
  withHint?: boolean;
  whiteWhenFilled?: boolean;
  successBorder?: boolean;
  whiteTextDisabled?: boolean;
};

export const ScInputStructure = styled.div<ScInputStructureProps>`
  position: relative;
  display: flex;
  flex-direction: column;

  .input-container {
    position: relative;
    height: ${({ height }) => (height ? `${height}px` : '48px')};
    width: 100%;
    border-radius: ${({ isInputFocused, withHint }) =>
      isInputFocused && withHint ? '0 0 12px 12px' : '12px'};

    background-color: ${({ whiteWhenFilled, theme, grayLight }) => {
      switch (true) {
        case whiteWhenFilled:
          return theme.whiteColor;
        case grayLight:
          return theme.grayColor5;
        default:
          return theme.grayColor4;
      }
    }};

    input:disabled,
    textarea:disabled {
      color: ${({ whiteTextDisabled, theme }) =>
        whiteTextDisabled ? theme.whiteColor : theme.grayColor6};

      -webkit-text-fill-color: ${({ whiteTextDisabled, theme }) =>
        whiteTextDisabled ? theme.whiteColor : theme.grayColor6};

      ${({ whiteTextDisabled }) =>
        whiteTextDisabled &&
        css`
          opacity: 1;
        `}
    }

    .addons {
      position: absolute;
      z-index: 2;
      right: 16px;
      top: 50%;
      transform: translate(0, -50%);
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    ${({ theme, isInputFocused, successBorder, error, withHint, noBorder }) =>
      !noBorder &&
      css`
        & > .border {
          position: absolute;
          z-index: 1;
          inset: 0;
          border-radius: ${
            isInputFocused && withHint ? '0 0 12px 12px' : '12px'
          };
          padding: 2px;
          -webkit-mask: linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background: ${(() => {
              switch (true) {
                case isInputFocused && error:
                  return theme.pinkRougeGradient;
                case isInputFocused && withHint:
                  return theme.greenMarineGradient;
                case successBorder:
                  return theme.greenMarineGradient;
                case !isInputFocused && error:
                  return theme.pinkRougeGradient;
                case isInputFocused:
                  return theme.greenColor;
                case error:
                  return theme.pinkRougeGradient;
                default:
                  return 'transparent';
              }
            })()};
          }
        }
      `}

    @media (hover: hover) {
      &:hover {
        input::placeholder,
        textarea::placeholder,
        ${ScAnimatedPlaceholder} {
          color: ${({ theme }) => theme.whiteColor};
        }
      }
    }
  }
`;

export const ScFieldStructure = styled.div`
  position: relative;
  min-width: 0;

  .field-container {
    position: relative;
  }
`;

export const ScError = styled.div<{ hideErrorStyles?: boolean }>`
  color: ${({ theme }) => theme.pinkColor};
  font-size: 12px;
  line-height: 18px;
  padding: 0 16px;
  margin-top: ${({ hideErrorStyles }) => (hideErrorStyles ? '0px' : '2px')};
  position: relative;

  &::first-letter {
    text-transform: capitalize;
  }
`;
